import { css } from '@emotion/react'
import React from 'react'
import { friendlyTimeZoneLabel } from '../../../util/crossZoneConfirm'
import Modal from '../misc/Modal'

// Phase 10 (design rev.5 §5) — the cross-zone explicit save confirmation.
//
// Shown ONLY when the editor's browser zone differs from the zone the event
// WILL ACTUALLY BE STORED IN (create: the Phase-7 resolvedTimeZone query;
// edit: the Phase-10b editResolvedTimeZone query). Never shown to the in-zone majority, and
// never before a venue/community is selected (the caller gates that — see
// shouldConfirmCrossZone / useCrossZoneConfirm). The zone shown here is the
// SERVER-resolved IANA value mapped to a friendly label; it is never a client
// guess. friendlyTimeZoneLabel falls back to the raw IANA so an unmapped zone
// is still stated truthfully and submit is never blocked on labelling.

type Props = {
  isOpen: boolean
  // Server-resolved IANA zone (authoritative). Displayed via friendly label.
  resolvedZone: string
  // Human-readable date + time of the event being saved, e.g.
  // "The event is from 1:00pm to 4:00pm on May 22, 2026." — built by the
  // caller from the form model via `eventsSummary`, so the submitter can
  // confirm exactly WHAT and WHEN they are saving. Optional: the confirm is
  // still truthful (and still gates submit) if it is absent.
  whenLabel?: string | null
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
}

const CrossZoneConfirmModal: React.FC<Props> = ({
  isOpen,
  resolvedZone,
  whenLabel,
  onConfirm,
  onCancel,
  loading,
}) => {
  const zoneLabel = friendlyTimeZoneLabel(resolvedZone)

  return (
    <Modal isOpen={isOpen} onRequestClose={onCancel}>
      <h2
        css={css`
          margin: 0 0 8px 0;
          font-size: 20px;
          color: #102638;
        `}
      >
        Confirm the event&apos;s time zone
      </h2>
      <p
        css={css`
          margin: 0 0 24px 0;
          color: #6b7280;
          font-size: 14px;
          line-height: 1.6;
        `}
      >
        Your computer is in a different time zone than this event&apos;s location. The time you enter is
        saved as the local time at the venue — it is not converted to your computer&apos;s zone.
        <br />
        <br />
        {whenLabel ? (
          <>
            <strong>{whenLabel}</strong>
            <br />
          </>
        ) : null}
        It will be saved in <strong>{zoneLabel}</strong>.
      </p>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        `}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          css={css`
            background: transparent;
            color: #6b7280;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            padding: 12px 20px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.15s ease-out;
            &:hover:not(:disabled) {
              background: #f9fafb;
              border-color: #9ca3af;
            }
          `}
        >
          Go back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          css={css`
            background: #a855f7;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 12px 24px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.15s ease-out;
            &:hover:not(:disabled) {
              background: #9333ea;
            }
            &:disabled {
              opacity: 0.7;
              cursor: not-allowed;
            }
          `}
        >
          {loading ? 'Saving...' : 'Yes, save it'}
        </button>
      </div>
    </Modal>
  )
}

export default CrossZoneConfirmModal
